import { atom } from 'recoil';
import type { RecoilState } from 'recoil';
import type { StatusAuditLogs } from 'hooks/useWorklistItemAuditLogs';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import type { WorklistSurfaceType } from './types';
import type {
  FilterStateType,
  FilterDefinitionType,
  FilterValueType,
  WorklistFiltersType,
} from '../WorklistFilters/types';

// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<{ loading: false; count: number; }>' is not assignable to type 'RecoilState<{ loading: boolean; count: number; }>'.
export const claimedItemsCountState: RecoilState<{
  loading: boolean;
  count: number;
}> = atom({
  key: 'worklist.claimedItemsCountAtom',
  default: {
    loading: false,
    count: 0,
  },
});

export type ClaimCaseDialogState = {
  open: boolean;
  caseId: string;
  fullName: string;
  action: 'QUEUE' | 'READ' | '';
  callback?: () => void;
  isAddendum?: boolean;
};

export const claimCaseDialogStateDefault = {
  isAddendum: false,
  open: false,
  caseId: '',
  fullName: '',
  action: '',
  callback: () => {},
} as const;

// @ts-expect-error [EN-10263] - TS2322 - Type 'RecoilState<{ readonly isAddendum: false; readonly open: false; readonly caseId: ""; readonly fullName: ""; readonly action: ""; readonly callback: () => void; }>' is not assignable to type 'RecoilState<ClaimCaseDialogState>'.
export const claimCaseDialogState: RecoilState<ClaimCaseDialogState> = atom({
  key: 'worklist.claimCaseDialogState',
  default: claimCaseDialogStateDefault,
});

type CaseAuditLogModalState = {
  open: boolean;
  auditLogs: StatusAuditLogs;
};

export const caseAuditLogModalStateDefault: CaseAuditLogModalState = {
  open: false,
  auditLogs: {
    statusChanges: [],
    customStatusChanges: [],
  },
};

export const caseAuditLogModalState: RecoilState<CaseAuditLogModalState> = atom({
  key: 'worklist.caseAuditLogModalState',
  default: caseAuditLogModalStateDefault,
});

type WorklistAutoLoadState = {
  surface: WorklistSurfaceType | null | undefined;
  searchParamsString: string | null | undefined;
  filters: FilterStateType | null | undefined;
  availableFilters: FilterDefinitionType | null | undefined;
  searchForSurface: string;
};

export const worklistAutoLoadStateDefault: WorklistAutoLoadState = {
  surface: null,
  searchParamsString: null,
  filters: null,
  availableFilters: null,
  searchForSurface: '',
};

export const worklistAutoLoadState: RecoilState<WorklistAutoLoadState> = atom({
  key: 'worklistAutoLoadState',
  default: worklistAutoLoadStateDefault,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const patientJacketShowRelevantPriorsOnlyState: RecoilState<boolean> = atom({
  key: 'patientJacketShowRelevantPriorsOnlyState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const assignExamDialogState: RecoilState<boolean> = atom({
  key: 'assignExamDialogIsOpen',
  default: false,
});

export const CUSTOM_WORKLIST_DIALOG_MODE = {
  CREATE: 'create',
  UPDATE: 'update',
  CLONE: 'clone',
} as const;

export type CustomWorklistDialogMode =
  (typeof CUSTOM_WORKLIST_DIALOG_MODE)[keyof typeof CUSTOM_WORKLIST_DIALOG_MODE];

export type advancedSettingsType = {
  statsToTop: boolean;
  showExamsCount: boolean;
  lockFilters: boolean;
};

export const defaultAdvancedSettings: advancedSettingsType = {
  statsToTop: true,
  showExamsCount: false,
  lockFilters: false,
} as const;

export type CustomWorklistDialogState = {
  isOpen: boolean;
  mode: CustomWorklistDialogMode;
  worklist: { smid: string; name: string } | null;
  advancedSettings: advancedSettingsType;
  initialFilters?: Partial<Record<WorklistFiltersType, FilterValueType>>;
};

export const customWorklistDialogState = atom<CustomWorklistDialogState>({
  key: 'customWorklistDialogState',
  default: {
    isOpen: false,
    mode: CUSTOM_WORKLIST_DIALOG_MODE.CREATE,
    worklist: null,
    advancedSettings: defaultAdvancedSettings,
    initialFilters: {},
  },
});

export const activeCustomWorklistIDsState = atom<string[]>({
  key: 'activeCustomWorklistIDsState',
  default: [],
});

export const activeCustomWorklistFiltersState: RecoilState<FilterStateType | null> = atom({
  key: 'activeCustomWorklistState',
  default: null as FilterStateType | null,
});
